// ============================================
// Trait Engine — TARS-Style Prompt Calibration
//
// Converts NurseTraitProfile slider values (0-100)
// into prompt fragments injected into the nurse
// system prompt before each turn.
// ============================================

import type { NurseTraitProfile, RegisterType } from '../../types/patientJourney';
import type { PJEConfig } from './pjeConfig';
import { getPJEConfig } from './pjeConfig';

// =====================================================================
// Trait → Prompt Fragment Mapping
// =====================================================================

/**
 * Generate empathy calibration prompt fragment.
 * Low = clinical, objective. High = warm, validating.
 */
function empathyFragment(value: number): string {
  if (value >= 90) {
    return '- EMPATHY: MAXIMUM — Lead with emotional validation before ANY data. Use phrases like "I hear you" and "That makes complete sense." Translate every data point into felt experience. Minimize clinical framing.';
  }
  if (value >= 70) {
    return '- EMPATHY: HIGH — Balance data with emotional anchoring. Validate feelings before explaining numbers. Connect biomarker values to daily life impact.';
  }
  if (value >= 50) {
    return '- EMPATHY: MODERATE — Acknowledge feelings briefly, then prioritize data. Use warm but efficient language.';
  }
  if (value >= 30) {
    return '- EMPATHY: LOW — Focus on clinical data. Minimal emotional framing. Brief acknowledgments only when patient shows distress.';
  }
  return '- EMPATHY: MINIMAL — Pure clinical communication. Data-forward, no emotional language. Respond to distress with facts, not feelings.';
}

/**
 * Generate data density calibration prompt fragment.
 * Controls how many biomarker references per response.
 */
function dataDensityFragment(value: number): string {
  const targetRefs = Math.max(1, Math.round(value / 15));
  if (value >= 80) {
    return `- DATA DENSITY: MAXIMUM — Reference ${targetRefs}+ biomarker values per response. Use structured tables when presenting 3+ values. Include exact numbers, units, and range comparisons.`;
  }
  if (value >= 60) {
    return `- DATA DENSITY: HIGH — Reference ${targetRefs}-${targetRefs + 1} biomarker values per response. Name, value, and status for each. Use inline data references.`;
  }
  if (value >= 40) {
    return `- DATA DENSITY: MODERATE — Reference ${targetRefs}-${targetRefs + 1} key biomarker values per response. Summarize groups (e.g., "your thyroid markers") rather than listing all individually.`;
  }
  if (value >= 20) {
    return `- DATA DENSITY: LIGHT — Reference ${targetRefs} biomarker value per response max. Summarize patterns in plain language rather than citing specific numbers.`;
  }
  return '- DATA DENSITY: MINIMAL — Avoid specific numbers unless directly asked. Describe results in qualitative terms ("your iron is running low" vs "ferritin at 22 ng/mL").';
}

/**
 * Generate explanation depth calibration prompt fragment.
 * Controls jargon level and how much clinical reasoning is shown.
 */
function explanationDepthFragment(value: number): string {
  if (value >= 80) {
    return '- EXPLANATION: THOROUGH — Explain WHY each range matters. Compare standard vs functional vs optimal ranges explicitly. Use clinical reasoning chains ("Low ferritin + low B12 suggests absorption issue, which explains..."). Define every technical term on first use.';
  }
  if (value >= 60) {
    return '- EXPLANATION: DETAILED — Explain key concepts and range differences. Define medical terms inline. Show reasoning for the most important findings.';
  }
  if (value >= 40) {
    return '- EXPLANATION: BALANCED — Explain significant findings. Define jargon only when first introduced. Assume moderate health literacy.';
  }
  if (value >= 20) {
    return '- EXPLANATION: BRIEF — High-level summaries. Skip range explanations unless asked. Use simple, everyday language.';
  }
  return '- EXPLANATION: MINIMAL — Headlines only. No detailed reasoning unless specifically requested. Maximum simplicity.';
}

/**
 * Generate product timing calibration prompt fragment.
 * Controls when the nurse introduces protocol/supplement recommendations.
 * Low value = early introduction. High value = late introduction.
 */
function productTimingFragment(value: number): string {
  if (value >= 80) {
    return '- PRODUCT TIMING: LATE — Do NOT introduce any supplement or protocol recommendations until the patient has fully discussed goals, concerns, and asked questions about their data. Minimum 6 turns of pure education before any product mention. Always use explicit transition language: "Now that we\'ve reviewed your labs thoroughly, would you like to hear about what might help?"';
  }
  if (value >= 60) {
    return '- PRODUCT TIMING: MODERATE-LATE — Introduce protocol after completing data presentation AND goals discussion. Use transition language before mentioning products. Minimum 4 turns before product mention.';
  }
  if (value >= 40) {
    return '- PRODUCT TIMING: BALANCED — Introduce protocol naturally after data presentation. Brief transition phrase before product mentions. Protocol can appear by turn 3-4.';
  }
  if (value >= 20) {
    return '- PRODUCT TIMING: MODERATE-EARLY — Weave protocol recommendations into data presentation where clinically relevant. Minimal transition needed. Protocol by turn 2-3.';
  }
  return '- PRODUCT TIMING: EARLY — Integrate product recommendations directly with findings. "Your ferritin is low — here\'s what we recommend." No separate transition needed.';
}

/**
 * Generate verification frequency calibration prompt fragment.
 * Controls how often the nurse checks patient understanding.
 */
function verificationFreqFragment(value: number): string {
  if (value >= 80) {
    return '- VERIFICATION: FREQUENT — Ask a clarifying or comprehension question after EVERY substantive response. Examples: "Does that make sense?", "What questions do you have about that?", "Can you tell me back what you understood about your ferritin level?" Require patient to reference a specific value before proceeding to protocol.';
  }
  if (value >= 60) {
    return '- VERIFICATION: REGULAR — Ask a follow-up question every 2-3 responses. Check understanding before major transitions (data→goals, goals→protocol). Ask at least one question requiring the patient to reference a specific value.';
  }
  if (value >= 40) {
    return '- VERIFICATION: MODERATE — Ask follow-up questions at major transition points. Check for confusion when presenting complex multi-marker correlations.';
  }
  if (value >= 20) {
    return '- VERIFICATION: LIGHT — Ask follow-up questions only when presenting critical findings or before protocol recommendations.';
  }
  return '- VERIFICATION: MINIMAL — Trust patient engagement. Only ask for clarification when the patient seems confused or disengaged.';
}

/**
 * Generate urgency calibration prompt fragment.
 * Controls how alarmed the nurse sounds and how quickly it escalates.
 */
function urgencyCalibrationFragment(value: number): string {
  if (value >= 80) {
    return '- URGENCY: HIGH — Flag any out-of-range marker prominently. Use language like "This needs attention" for suboptimal, "This is concerning" for elevated, "This requires immediate follow-up" for critical. Accelerate Phase 1 to reach protocol faster. Push for next steps.';
  }
  if (value >= 60) {
    return '- URGENCY: MODERATE-HIGH — Clearly flag elevated and critical markers with appropriate concern. Use measured but direct language. Prioritize critical findings in presentation order.';
  }
  if (value >= 40) {
    return '- URGENCY: BALANCED — Present findings in clinical context without over-alarming. Distinguish between "worth watching" (suboptimal), "worth addressing" (elevated), and "needs attention" (critical).';
  }
  if (value >= 20) {
    return '- URGENCY: CALM — Frame all findings positively when possible. "Your body is giving us useful information." Use encouraging language even for concerning results. Avoid alarm-triggering words.';
  }
  return '- URGENCY: VERY CALM — Maximum reassurance. Frame everything as manageable and routine. "These are common patterns we see, and they\'re very addressable." Never use words like concerning, alarming, or worrying.';
}

// =====================================================================
// Public API
// =====================================================================

/**
 * Build the complete trait calibration prompt section from a NurseTraitProfile.
 * Returns a string to be injected into the nurse system prompt.
 *
 * If no profile is provided, reads from PJEConfig (which may have been
 * set via the Nurse Tuning UI).
 */
export function buildTraitCalibrationPrompt(
  profile?: NurseTraitProfile,
  config?: PJEConfig,
): string {
  const cfg = config || getPJEConfig();
  const p = profile || cfg.traitProfile;

  // No trait profile configured — return empty (backward compat)
  if (!p) return '';

  const fragments = [
    '',
    'TRAIT CALIBRATION (behavioral priority overrides):',
    empathyFragment(p.empathy),
    dataDensityFragment(p.dataDensity),
    explanationDepthFragment(p.explanationDepth),
    productTimingFragment(p.productTiming),
    verificationFreqFragment(p.verificationFreq),
    urgencyCalibrationFragment(p.urgencyCalibration),
  ];

  return fragments.join('\n');
}

/**
 * Build register-specific trait modifiers.
 * Adjusts the trait emphasis based on which register is active.
 * For example, Analyst register amplifies data density,
 * Guide register amplifies empathy.
 */
export function buildRegisterTraitModifiers(
  profile: NurseTraitProfile,
  register: RegisterType,
): string {
  // Create a register-adjusted copy of the profile
  const adjusted = { ...profile };

  switch (register) {
    case 'analyst':
      // Analyst amplifies data traits, dampens empathy
      adjusted.dataDensity = Math.min(100, adjusted.dataDensity + 15);
      adjusted.explanationDepth = Math.min(100, adjusted.explanationDepth + 10);
      adjusted.empathy = Math.max(0, adjusted.empathy - 15);
      break;
    case 'guide':
      // Guide amplifies empathy traits, dampens data
      adjusted.empathy = Math.min(100, adjusted.empathy + 20);
      adjusted.verificationFreq = Math.min(100, adjusted.verificationFreq + 10);
      adjusted.dataDensity = Math.max(0, adjusted.dataDensity - 15);
      break;
    case 'blended':
      // Blended uses traits as-is (no adjustment)
      break;
  }

  return buildTraitCalibrationPrompt(adjusted);
}

/**
 * Compute a human-readable summary of the trait profile.
 * Used in test run metadata and logging.
 */
export function summarizeTraitProfile(profile: NurseTraitProfile): string {
  const level = (v: number) => v >= 80 ? 'HIGH' : v >= 50 ? 'MED' : 'LOW';
  return [
    `Emp:${level(profile.empathy)}(${profile.empathy})`,
    `Data:${level(profile.dataDensity)}(${profile.dataDensity})`,
    `Expl:${level(profile.explanationDepth)}(${profile.explanationDepth})`,
    `Prod:${level(profile.productTiming)}(${profile.productTiming})`,
    `Verf:${level(profile.verificationFreq)}(${profile.verificationFreq})`,
    `Urgn:${level(profile.urgencyCalibration)}(${profile.urgencyCalibration})`,
  ].join(' | ');
}
